(() => {
  'use strict';
  const state = { lastFocus: null };
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));

  function focusable(container) {
    return qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container).filter(el => !el.hidden && el.offsetParent !== null);
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function openLayer(layer, trigger) {
    if (!layer) return;
    state.lastFocus = trigger || document.activeElement;
    layer.hidden = false;
    requestAnimationFrame(() => layer.classList.add('is-open'));
    document.body.classList.add(layer.matches('.modal') ? 'modal-open' : 'drawer-open');
    const panel = qs('[role="dialog"]', layer);
    if (panel) setTimeout(() => panel.focus(), 50);
  }

  function closeLayer(layer) {
    if (!layer) return;
    layer.classList.remove('is-open');
    setTimeout(() => { layer.hidden = true; }, 300);
    document.body.classList.remove('drawer-open', 'modal-open');
    qsa(`[aria-controls="${layer.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
    if (state.lastFocus && document.contains(state.lastFocus)) state.lastFocus.focus();
  }

  function initLayers(root = document) {
    qsa('[data-drawer-open]', root).forEach(trigger => {
      if (trigger.dataset.bound) return;
      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', () => {
        const layer = document.getElementById(trigger.dataset.drawerOpen);
        trigger.setAttribute('aria-expanded', 'true'); openLayer(layer, trigger);
      });
    });
    qsa('[data-modal-open]', root).forEach(trigger => {
      if (trigger.dataset.bound) return;
      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', () => openLayer(document.getElementById(trigger.dataset.modalOpen), trigger));
    });
    qsa('[data-drawer],[data-modal]', root).forEach(layer => {
      if (layer.dataset.layerBound) return;
      layer.dataset.layerBound = 'true';
      qsa('[data-drawer-close],[data-modal-close]', layer).forEach(close => close.addEventListener('click', () => closeLayer(layer)));
      layer.addEventListener('keydown', event => {
        if (event.key === 'Escape') closeLayer(layer);
        trapFocus(event, layer);
      });
    });
  }

  function initHeader(root = document) {
    const header = qs('[data-header]', root) || qs('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--header-bottom', `${header.getBoundingClientRect().bottom}px`);
    qsa('.header__menu-link[aria-expanded]', header).forEach(button => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.has-dropdown');
        const open = item.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
      });
    });
  }

  async function addToCart(form, button) {
    button.disabled = true; form.classList.add('is-loading');
    try {
      const response = await fetch(window.themeRoutes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add this item.');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { open: true } }));
      const live = qs('[data-live-region]'); if (live) live.textContent = `${data.product_title || 'Item'} added to cart.`;
    } catch (error) {
      const target = qs('[data-product-error]', form); if (target) { target.hidden = false; target.textContent = error.message; } else alert(error.message);
    } finally { button.disabled = false; form.classList.remove('is-loading'); }
  }

  function initQuickAdd(root = document) {
    qsa('[data-quick-add]', root).forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', event => { event.preventDefault(); addToCart(form, qs('[type="submit"]', form)); });
    });
  }

  function initProductRecommendations(root = document) {
    qsa('product-recommendations:not([data-loaded])', root).forEach(async el => {
      el.dataset.loaded = 'true';
      if (!el.dataset.productId) return;
      try {
        const separator = el.dataset.url.includes('?') ? '&' : '?';
        const response = await fetch(`${el.dataset.url}${separator}product_id=${encodeURIComponent(el.dataset.productId)}&section_id=${encodeURIComponent(el.closest('.shopify-section')?.id.replace('shopify-section-', '') || '')}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = qs('product-recommendations', doc);
        if (replacement && replacement.innerHTML.trim()) { el.innerHTML = replacement.innerHTML; initQuickAdd(el); }
      } catch (_) { el.hidden = true; }
    });
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'CAD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `$${(cents / 100).toFixed(2)}`; }
  }

  function initRecentlyViewed(root = document) {
    qsa('[data-recently-viewed]', root).forEach(async section => {
      if (section.dataset.bound) return; section.dataset.bound = 'true';
      const current = section.dataset.currentProduct;
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('pettrail-recent') || '[]'); } catch (_) {}
      const previous = handles.filter(handle => handle && handle !== current).slice(0, Number(section.dataset.limit || 4));
      if (current) localStorage.setItem('pettrail-recent', JSON.stringify([current, ...handles.filter(h => h !== current)].slice(0, 10)));
      if (!previous.length) return;
      try {
        const products = await Promise.all(previous.map(handle => fetch(`${window.themeRoutes.root}products/${encodeURIComponent(handle)}.js`).then(r => r.ok ? r.json() : null)));
        const grid = qs('[data-recent-grid]', section);
        products.filter(Boolean).forEach(product => {
          const article = document.createElement('article'); article.className = 'product-card';
          const image = product.featured_image ? `<img class="product-card__image product-card__image--primary" src="${escapeHtml(product.featured_image)}&width=720" alt="${escapeHtml(product.title)}" loading="lazy">` : '';
          article.innerHTML = `<a class="product-card__media ratio-square" href="${escapeHtml(product.url)}">${image}</a><div class="product-card__info"><h3><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3><div class="price">${formatMoney(product.price)}</div></div>`;
          grid.appendChild(article);
        });
        if (grid.children.length) section.hidden = false;
      } catch (_) {}
    });
  }

  function initCountdown(root = document) {
    qsa('[data-countdown]', root).forEach(section => {
      if (section.dataset.bound || !section.dataset.countdown) return; section.dataset.bound = 'true';
      const end = new Date(section.dataset.countdown).getTime(); if (Number.isNaN(end)) return;
      const tick = () => {
        const distance = end - Date.now();
        if (distance <= 0) { const clock = qs('.countdown__clock', section); if (clock) clock.innerHTML = `<strong>${escapeHtml(section.dataset.expiredMessage)}</strong>`; clearInterval(timer); return; }
        const values = { days: Math.floor(distance / 86400000), hours: Math.floor(distance / 3600000) % 24, minutes: Math.floor(distance / 60000) % 60, seconds: Math.floor(distance / 1000) % 60 };
        Object.entries(values).forEach(([key, value]) => { const node = qs(`[data-${key}]`, section); if (node) node.textContent = String(value).padStart(2, '0'); });
      };
      tick(); const timer = setInterval(tick, 1000);
    });
  }

  function initSlideshows(root = document) {
    qsa('[data-slideshow]', root).forEach(slideshow => {
      if (slideshow.dataset.bound) return; slideshow.dataset.bound = 'true';
      const slides = qsa('[data-slide]', slideshow), dots = qsa('[data-slide-to]', slideshow); if (slides.length < 2) return;
      let current = 0, timer;
      const show = index => { current = (index + slides.length) % slides.length; slides.forEach((slide, i) => { const active = i === current; slide.classList.toggle('is-active', active); slide.setAttribute('aria-hidden', String(!active)); }); dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current)); };
      const stop = () => clearInterval(timer); const start = () => { stop(); if (slideshow.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => show(current + 1), Number(slideshow.dataset.speed || 6000)); };
      qs('[data-slide-prev]', slideshow)?.addEventListener('click', () => { show(current - 1); start(); }); qs('[data-slide-next]', slideshow)?.addEventListener('click', () => { show(current + 1); start(); }); dots.forEach(dot => dot.addEventListener('click', () => { show(Number(dot.dataset.slideTo)); start(); })); slideshow.addEventListener('mouseenter', stop); slideshow.addEventListener('mouseleave', start); start();
    });
  }

  function initOverlays() {
    const popup = qs('[data-email-popup]');
    if (popup && !sessionStorage.getItem('pettrail-popup-seen')) setTimeout(() => { sessionStorage.setItem('pettrail-popup-seen', '1'); openLayer(popup); }, Number(popup.dataset.delay || 8000));
    const cookie = qs('[data-cookie-notice]');
    if (cookie && !localStorage.getItem('pettrail-cookie-choice')) cookie.hidden = false;
    qsa('[data-cookie-accept],[data-cookie-dismiss]').forEach(button => button.addEventListener('click', () => { localStorage.setItem('pettrail-cookie-choice', button.hasAttribute('data-cookie-accept') ? 'accepted' : 'essential'); if (cookie) cookie.hidden = true; }));
  }

  function initUtilities(root = document) {
    if (matchMedia('(max-width: 749px)').matches) qsa('.footer-block details[open]', root).forEach(detail => detail.removeAttribute('open'));
    qsa('.announcement-bar--ticker .announcement-bar__inner', root).forEach(inner => { if (!inner.dataset.tickerCloned && matchMedia('(max-width: 749px)').matches) { inner.dataset.tickerCloned = 'true'; inner.innerHTML += inner.innerHTML; } });
    qsa('[data-copy-link]', root).forEach(button => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', async () => { try { await navigator.clipboard.writeText(button.dataset.url); button.textContent = 'Copied'; } catch (_) {} }); } });
    qsa('[data-login-toggle]', root).forEach(button => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', () => { const login = qs('#CustomerLoginForm'), recover = qs('#RecoverPasswordForm'); if (login && recover) { login.hidden = !login.hidden; recover.hidden = !recover.hidden; } }); } });
    qsa('[data-address-toggle]', root).forEach(button => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', () => { const target = document.getElementById(button.dataset.addressToggle); if (target) target.hidden = !target.hidden; }); } });
    qsa('form[data-confirm-message]', root).forEach(form => { if (!form.dataset.bound) { form.dataset.bound = 'true'; form.addEventListener('submit', event => { if (!confirm(form.dataset.confirmMessage)) event.preventDefault(); }); } });
    qsa('[data-address-country]', root).forEach(country => { if (country.dataset.bound) return; country.dataset.bound = 'true'; const province = country.closest('form')?.querySelector('[data-address-province]'); const update = () => { if (!province) return; const option = country.options[country.selectedIndex]; let provinces = []; try { provinces = JSON.parse(option.dataset.provinces || '[]'); } catch (_) {} province.innerHTML = ''; provinces.forEach(([value, label]) => { const item = new Option(label, value, false, value === province.dataset.default); province.add(item); }); province.closest('label').hidden = !provinces.length; }; country.addEventListener('change', update); update(); });
    qsa('[data-grid-columns]', root).forEach(button => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', () => { const grid = qs('[data-product-grid]'); if (!grid) return; grid.className = grid.className.replace(/columns-\d/, `columns-${button.dataset.gridColumns}`); }); } });
    const sort = qs('[data-sort-select]', root); if (sort && !sort.dataset.bound) { sort.dataset.bound = 'true'; sort.addEventListener('change', () => sort.form.submit()); }
    qsa('[data-filter-close]', root).forEach(button => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', () => { qs('[data-filter-drawer]')?.classList.remove('is-open'); const opener = qs('[data-filter-open]'); opener?.setAttribute('aria-expanded','false'); document.body.classList.remove('drawer-open'); opener?.focus(); }); } });
    qsa('[data-filter-drawer]', root).forEach(drawer => { if (!drawer.dataset.bound) { drawer.dataset.bound = 'true'; drawer.addEventListener('keydown', event => { if (event.key === 'Escape') qs('[data-filter-close]', drawer)?.click(); else trapFocus(event, drawer); }); } });
    const filterOpen = qs('[data-filter-open]', root); if (filterOpen && !filterOpen.dataset.filterBound) { filterOpen.dataset.filterBound = 'true'; filterOpen.addEventListener('click', () => { const drawer = qs('[data-filter-drawer]'); drawer?.classList.add('is-open'); filterOpen.setAttribute('aria-expanded','true'); document.body.classList.add('drawer-open'); setTimeout(() => qs('[role="dialog"]', drawer)?.focus(), 50); }); }
  }

  function initMotion(root = document) {
    const items = qsa('[data-animate]:not(.is-visible)', root);
    if (!items.length || matchMedia('(prefers-reduced-motion: reduce)').matches) { items.forEach(el => el.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }), { rootMargin: '0px 0px -8% 0px' });
    items.forEach(item => observer.observe(item));
  }

  function init(root = document) { initLayers(root); initHeader(root); initQuickAdd(root); initProductRecommendations(root); initRecentlyViewed(root); initCountdown(root); initSlideshows(root); initUtilities(root); initMotion(root); }
  document.addEventListener('DOMContentLoaded', () => { init(); initOverlays(); const back = qs('[data-back-to-top]'); if (back) { addEventListener('scroll', () => back.classList.toggle('is-visible', scrollY > 500), { passive: true }); back.addEventListener('click', () => scrollTo({ top: 0, behavior: 'smooth' })); } });
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.PetTrailTheme = { openLayer, closeLayer, init, formatMoney, escapeHtml };
})();
